import {
  TimesheetsToggler,
  PastSprintsDivider,
  ShowLastSprintCheckbox,
  startsPastSprints,
  SprintSection,
  DateRangeFilter,
  ClearFilterButton,
  FilterField,
  Pagination,
} from '@app/components/core';
import { memberReportingPath, membersReportingListPath, sprintReportingPath } from '@app/constants/url';
import { filterBarClass, pageHeaderRowClass, pageTitleClass } from '@app/constants/styles';
import { shortDate, toFixedHours } from '@app/utils';
import { Roles } from '@app/utils/types';
import { MembershipEntity, MembershipStatus } from '@data';
import { Button } from '@ui/components/ui/button';
import { useListPmMembers, usePmMembersBySprint, useSendMembershipReminder } from '@data/hooks/membership';
import { useOrganizationChapters } from '@data/hooks/organization';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationId, useSectionToggle, useSprintPaging } from '@app/utils/hooks';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { Loader } from '@ui';
import { Bell, ChevronRight } from 'lucide-react';
import cx from 'classnames';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable, DataTableColumnHeader } from '@ui/components/ui/data-table';
import { MultiSelectField } from '@ui/components/ui/multi-select';

type MembersReportingRow = Omit<MembershipEntity, 'chapters'> & {
  member: string;
  product: number;
  chapters: string;
  hours: number;
};

// How long the receipt holds at full strength before it starts fading. Long enough to read, short
// enough that the button is honest again by the time you look at the next row.
const PING_CONFIRMATION_MS = 2000;

/**
 * Ping, and the receipt for it.
 *
 * The button is only ever as wide as the words it is showing, and it changes size on its own terms: it
 * widens to take the receipt, holds it, fades the words out, and only gives the width back once they
 * are nearly gone. Reserving room for the longer label instead left every idle button oversized, and
 * swapping the string outright made the whole row jump.
 *
 * Both labels share one grid cell and open from 0fr to 1fr, so the button measures whichever is
 * currently open and animates between the two rather than snapping.
 *
 * The growth is taken out of the layout: an invisible copy at the resting size holds the column open,
 * and the real button floats over it, centred. Otherwise the column widened with the button and pushed
 * every other column across — the row no longer jumped when the text changed, but the whole table did.
 *
 * The receipt is temporary and lives here rather than in the query cache. It says something about this
 * click, not about the member: nothing on the server changes, and a row that reads "Ping sent!" for the
 * rest of the session is a stale claim about a Slack message somebody may already have acted on.
 */
const PingButton = ({ disabled, onPing }: { disabled?: boolean; onPing: () => Promise<unknown> }): ReactElement => {
  const { t } = useTranslation();
  // A counter rather than a boolean: pinging again after the receipt has to restart the countdown, and
  // setting `true` over `true` would leave the first deadline running.
  const [pings, setPings] = useState(0);
  const [sending, setSending] = useState(false);
  const sent = pings > 0;

  useEffect(() => {
    if (!pings) return undefined;
    const timer = setTimeout(() => setPings(0), PING_CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [pings]);

  // The receipt waits for Slack to accept the message. It used to appear on the click itself, which told
  // people a nudge had gone out while the error toast beside it said the opposite.
  //
  // Nothing on the button moves while the request is in flight: greying it there and expanding it on the
  // reply split one change into two, a beat apart. The guard still blocks a second send — it just does
  // not announce itself.
  const ping = (): void => {
    if (sending) return;
    setSending(true);
    onPing()
      .then(() => setPings(count => count + 1))
      // The API's own message is already on screen through the global error toast, and the button simply
      // stays as it was — no receipt for something that never sent.
      .catch(() => undefined)
      .finally(() => setSending(false));
  };

  return (
    <span className="relative inline-flex">
      {/* Holds the column at the resting size, so nothing reflows while the receipt is open. */}
      <span aria-hidden className="invisible">
        <Button size="sm" tabIndex={-1}>
          <Bell />
          {t('ping')}
        </Button>
      </span>
      {/* Exactly the placeholder's box, with the button centred in it: once the button outgrows the box
          it overflows evenly on both sides, so the middle stays where it was. */}
      <span className="absolute inset-0 flex items-center justify-center">
        {/* Fades and widens in the same moment, when the receipt arrives, and comes back together too. */}
        <Button size="sm" disabled={disabled || sent} onClick={ping}>
          {/* Rings on the confirmed send, not the click: the swing is the receipt arriving, so it has to
              mean the same thing as the words next to it. The button is disabled while the receipt is up,
              so the class only ever goes on once per send and the animation restarts cleanly each time. */}
          <Bell className={cx('origin-top', sent && 'motion-safe:animate-bell-ring')} />
          <span className="grid text-center">
            {/* The receipt: opens with the button, then fades over 700ms. */}
            <span
              aria-hidden={!sent}
              className={cx(
                'col-start-1 row-start-1 grid transition-[grid-template-columns] ease-out',
                sent ? 'grid-cols-[1fr] duration-200' : 'grid-cols-[0fr] delay-500 duration-300',
              )}
            >
              <span
                className={cx(
                  'overflow-hidden whitespace-nowrap transition-opacity ease-out',
                  sent ? 'opacity-100 delay-100 duration-200' : 'opacity-0 duration-700',
                )}
              >
                {t('pingSent')}
              </span>
            </span>
            {/* "Ping" gets out of the way at once, and comes back with the shrink at 500ms — by which point
            the receipt has almost finished fading, so the two never overlap. */}
            <span
              aria-hidden={sent}
              className={cx(
                'col-start-1 row-start-1 grid transition-[grid-template-columns] ease-out',
                sent ? 'grid-cols-[0fr] delay-500 duration-300' : 'grid-cols-[1fr] duration-200',
              )}
            >
              <span
                className={cx(
                  'overflow-hidden whitespace-nowrap transition-opacity ease-out',
                  sent ? 'opacity-0 duration-150' : 'opacity-100 delay-500 duration-300',
                )}
              >
                {t('ping')}
              </span>
            </span>
          </span>
        </Button>
      </span>
    </span>
  );
};

function MembersReportingListLoaded({ organizationId }: { organizationId: string }): ReactElement {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showAllSprints, setShowAllSprints] = useSectionToggle('members-reporting-all-sprints', [
    sprintReportingPath(organizationId),
    membersReportingListPath(organizationId),
  ]);

  // includeDisabled: render labels for historical involvements pointing at chapters the admin
  // has since disabled. The picker (ReportModal) stays on the active-only default.
  const { data: chapters } = useOrganizationChapters(organizationId, { includeDisabled: true });

  const {
    sprints,
    baseSprint,
    listSprints,
    yearPages,
    pageCount,
    currentPage,
    setPage,
    fromMissing,
    toMissing,
    currentSprint,
    showsAllSprints,
  } = useSprintPaging({ from, to, showAllSprints });

  // No status filter: someone who has since left the company still worked the sprints they worked, and a
  // past sprint that quietly drops them reads as if the work was never done. They come through tagged
  // instead (see the member column). The sprint window rides on each sprint's own query, so what is
  // left here is the list-wide filtering, of which there is none.
  const membersPmFilters = useMemo(() => ({}), []);

  const { pages: memberPages, ask } = usePmMembersBySprint(
    organizationId,
    listSprints,
    membersPmFilters,
    !!organizationId && !!baseSprint,
  );

  const { mutateAsync: sendMembershipReminder } = useSendMembershipReminder(organizationId);

  const { t } = useTranslation();
  const transitionNavigate = useViewTransitionNav();

  // Name filter options — every (non-client) member of the org, independent of the sprint window. Departed
  // members are listed too, so you can single one out in the sprints they were still around for; the
  // dropdown says so under their name.
  const { data: allMembers } = useListPmMembers(organizationId, {});

  const nameOptions = useMemo(
    () =>
      (allMembers?.items ?? [])
        .filter(m => m.role !== Roles.client && !!m.user.id)
        .map(m => ({
          value: String(m.user.id),
          label: `${m.user.firstName || ''} ${m.user.lastName || ''}`.trim() || m.user.email || '',
          ...(m.user.status === MembershipStatus.deactivated && { description: t('tabs.deactivated') }),
        })),
    [allMembers, t],
  );

  /* eslint-disable react/no-unstable-nested-components -- TanStack Table column render props */
  const columns = useMemo<ColumnDef<MembersReportingRow>[]>(
    () => [
      {
        accessorKey: 'member',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('employee')} />,
        // Same weight the Team list gives a member's name, so the two member tables read as one thing.
        cell: ({ row }) => <span className="font-medium">{row.original.member}</span>,
      },
      {
        accessorKey: 'hours',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('totalHours')} />,
      },
      {
        accessorKey: 'emptyDaysCount',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('emptyDays')} />,
        // A count here is a call to chase the gap, and there is nobody to chase once someone has left — so
        // their row keeps the dash the table uses elsewhere for "nothing to say" rather than a red figure.
        cell: ({ row }) => {
          const { emptyDaysCount, user } = row.original;
          if (user.status === MembershipStatus.deactivated) return '-';
          return (
            <span className={emptyDaysCount > 1 ? 'font-bold text-destructive' : 'font-normal text-foreground'}>
              {emptyDaysCount}
            </span>
          );
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        header: '',
        // A ping is a Slack nudge to go and report the missing days, which is nothing to ask of someone who
        // has left. The button still holds the column's shape for their row, greyed out; the title sits on
        // the wrapper because a disabled button takes no pointer events of its own.
        cell: ({ row }) => {
          const isDeactivated = row.original.user.status === MembershipStatus.deactivated;
          return (
            // Only the button swallows the click; the surrounding cell space still navigates the row.
            <div
              className="flex flex-wrap gap-1"
              role="presentation"
              title={isDeactivated ? t('pingNeedsActiveMember') : undefined}
              onClick={e => e.stopPropagation()}
            >
              <PingButton disabled={isDeactivated} onPing={() => sendMembershipReminder({ id: row.original.id })} />
            </div>
          );
        },
      },
      {
        id: 'go',
        enableSorting: false,
        header: '',
        meta: { className: 'w-0 text-right' },
        cell: () => <ChevronRight className="size-4 text-muted-foreground" />,
      },
    ],
    [t, sendMembershipReminder],
  );
  /* eslint-enable react/no-unstable-nested-components */

  const getRowData = (memberships: MembershipEntity[]) =>
    memberships
      ? memberships
          .filter(x => x.role !== Roles.client)
          // A departed colleague earns their place in a sprint by having worked it. With nothing logged
          // there, the row says nothing except that they once existed, so it stays out.
          .filter(x => x.user.status !== MembershipStatus.deactivated || x.totalMinutes > 0)
          .filter(x => !selectedUserIds.length || selectedUserIds.includes(x.user.id ?? ''))
          .map(
            (x): MembersReportingRow => ({
              ...x,
              member: `${x.user.firstName || ''} ${x.user.lastName || ''}`,
              product: x.productsCount,
              chapters: x.chapters.map(chapter => chapters?.items?.find(c => c.role === chapter)?.text).join(', '),
              hours: toFixedHours(x.totalMinutes / 60),
            }),
          )
      : [];

  // How tall to draw a sprint that has not arrived yet, in rows as the table draws them: memberships
  // the filters leave out are not rows, and it is the rows that decide the height. Read straight
  // rather than memoised — `getRowData` closes over the filters, and a memo that did not list them
  // among its dependencies kept sizing placeholders for the view before the filter was applied.
  const loadedRows = memberPages.find(page => page.items)?.items;
  const placeholderRows = (loadedRows ? getRowData(loadedRows).length : 0) || Math.min(nameOptions.length || 5, 12);

  // Three distinct render states so we never show a perpetual loader:
  // - sprints undefined        -> still fetching the sprint list, contained loader.
  // - sprints loaded but empty  -> no sprint matches the active tab's window (e.g. the past
  //                               tab when no sprint precedes the current one), empty state.
  // - sprints + baseSprint OK   -> infinite scroll renders.
  // Loader is non-absolute so it sits inline instead of escaping over the page header.
  let listContent: ReactElement;
  if (!sprints) {
    listContent = (
      <div className="py-10">
        <Loader absolute={false} />
      </div>
    );
  } else if (!baseSprint) {
    listContent = <p className="py-10 text-center text-sm text-muted-foreground">{t('noSprintForTab')}</p>;
  } else {
    listContent = (
      // Fills the page frame, so a short year sends its pager to the foot rather than under the last row.
      <div className="flex flex-1 flex-col">
        {/* Keyed on the year page: every sprint of it is laid out at once, so the fade plays for the
            page rather than for whichever rows happened to arrive first. */}
        <div key={currentPage} className="duration-200 animate-in fade-in-0">
          {memberPages
            // A name filter can empty a sprint: drop its section before the map, so the rule reads a drawn one.
            .filter(page => !(page.items && !getRowData(page.items).length))
            .map((page, index, shown) => {
              const rows = page.items ? getRowData(page.items) : [];
              const startsPast = startsPastSprints(shown, index, currentSprint?.id);
              return (
                <React.Fragment key={page.sprint.id}>
                  {startsPast && <PastSprintsDivider />}
                  <SprintSection
                    title={`Sprint ${page.sprint.number} (${shortDate(page.sprint.startDate)} – ${shortDate(
                      page.sprint.endDate,
                    )})`}
                    columnCount={columns.length}
                    rowCount={placeholderRows}
                    isLoaded={!!page.items}
                    hasFailed={page.hasFailed}
                    onRetry={page.retry}
                    onNear={() => ask(page.sprint.id)}
                  >
                    <DataTable<MembersReportingRow>
                      columns={columns}
                      data={rows}
                      padded
                      initialSorting={[{ id: 'emptyDaysCount', desc: true }]}
                      getRowId={row => row.id}
                      // A departed colleague's row is there for the sprint's history, not to be acted on.
                      rowClassName={row =>
                        row.user.status === MembershipStatus.deactivated
                          ? 'text-muted-foreground/55 [&_svg]:opacity-55'
                          : undefined
                      }
                      // Carry the heading's sprint across, or the member's page opens on the most recent one.
                      onRowClick={row =>
                        transitionNavigate(memberReportingPath(organizationId, row.id || ''), 'forward', {
                          state: { focusSprintId: page.sprint.id },
                        })
                      }
                    />
                  </SprintSection>
                </React.Fragment>
              );
            })}
        </div>
        {showsAllSprints && (
          <Pagination
            className="mt-auto pt-8"
            page={currentPage}
            pageCount={pageCount}
            onChange={setPage}
            labels={yearPages.map(yearPage => String(yearPage.year))}
            reversed
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-1 flex-col">
      <div className={`${pageHeaderRowClass} mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between`}>
        <h1 className={pageTitleClass}>{t('titles.sprintReporting')}</h1>
        {/* Always: this route is gated to PMs and admins, so anyone reading it may open it. */}
        <TimesheetsToggler active="members" organizationId={organizationId} canSeeMembers />
      </div>

      <div className={`mb-6 ${filterBarClass}`}>
        <FilterField wide>
          <MultiSelectField
            label={t('titles.members')}
            options={nameOptions}
            value={selectedUserIds}
            onChange={setSelectedUserIds}
            emptyLabel={t('allMembers')}
            searchPlaceholder={t('search')}
            emptySearchLabel={t('noMatches')}
            renderCountSummary={count => t('nItemsSelected', { count })}
          />
        </FilterField>
        <DateRangeFilter
          idPrefix="members"
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          fromMissing={fromMissing}
          toMissing={toMissing}
          hideClear
        />
        <ShowLastSprintCheckbox
          id="members-reporting-all-sprints"
          showAll={showAllSprints}
          forced={Boolean(from && to)}
          onShowAllChange={setShowAllSprints}
        />
        {(selectedUserIds.length > 0 || (from && to)) && (
          <ClearFilterButton
            onClick={() => {
              setSelectedUserIds([]);
              setFrom('');
              setTo('');
              setShowAllSprints(false);
            }}
          />
        )}
      </div>

      {listContent}
    </div>
  );
}

const MembersReportingList = (): ReactElement => {
  const organizationId = useOrganizationId();

  if (!organizationId) {
    return <Loader />;
  }

  return <MembersReportingListLoaded organizationId={organizationId} />;
};

export default MembersReportingList;
